import React, { useState, useEffect } from 'react';
import ImageComponent from 'shared/ImageComponent';
import { convertDateFromApiToUserTimezone } from 'utils/utility_functions/timezone';
import '../mailbox.scss';
import { Tag } from 'primereact/tag';
import { UserDetailsContext } from 'context/userDetailsContext';
import { useContext } from 'react';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import { Tooltip } from 'primereact/tooltip';
import { DataScroller } from 'primereact/datascroller';

const MailList = ({
  allMails,
  selectedEmailThreadPk,
  setSelectedEmailThreadPkSk,
  handleLoadMore,
  loading,
  hasNext,
  paginationLoading,
  readEmails,
  setReadEmails,
  multiple = false,
  selectedEmails = [],
  setSelectedEmails = () => {},
}) => {
  const userContext = useContext(UserDetailsContext);
  const [scrollHeight, setScrollHeight] = useState('600px');

  // Calculate dynamic scrollHeight based on window.innerHeight
  // Reserve space for: header (~80px), ReconcileHeader (~120px), info banner (~40px),
  // tab header (~50px), search bar (~60px), padding/margins (~50px) = ~400px total
  useEffect(() => {
    const calculateScrollHeight = () => {
      const reservedSpace = 320; // Total reserved space for all UI elements above the list
      const calculatedHeight = window.innerHeight - reservedSpace;
      // Ensure minimum height of 400px
      const finalHeight = Math.max(400, calculatedHeight);
      setScrollHeight(`${finalHeight}px`);
    };

    // Calculate on mount
    calculateScrollHeight();

    // Recalculate on window resize
    window.addEventListener('resize', calculateScrollHeight);

    return () => {
      window.removeEventListener('resize', calculateScrollHeight);
    };
  }, []);

  const toggleEmailSelection = (pk, sk, email_thread_id) => {
    setSelectedEmails((prev) => {
      const exists = prev.some((email) => email.pk === pk && email.sk === sk);
      if (exists) {
        // If unchecking, remove it
        return prev?.filter((email) => email?.pk !== pk || email?.sk !== sk);
      } else {
        // If checking, add it and also ensure the currently selected mail is included
        const newSelection = [...prev, { pk, sk, email_thread_id }];
        // Find the currently selected mail and add it if not already in the array
        if (selectedEmailThreadPk) {
          const currentMail = allMails?.find((m) => m?.pk === selectedEmailThreadPk);
          if (currentMail) {
            const currentMailExists = newSelection.some((email) => email?.pk === currentMail?.pk && email?.sk === currentMail?.sk);
            if (!currentMailExists) {
              newSelection.push({
                pk: currentMail?.pk,
                sk: currentMail?.sk,
                email_thread_id: currentMail?.email_thread_id,
              });
            }
          }
        }
        return newSelection;
      }
    });
  };
  const handleEmailClick = (mail) => {
    setSelectedEmailThreadPkSk({ pk: mail?.pk, sk: mail?.sk });
    setReadEmails((prev) => ({ ...prev, [mail?.pk]: true }));
    // Remove the previously button-selected mail, keep manually checked mails, and add the new mail
    setSelectedEmails((prev) => {
      // Remove the previously button-selected mail (if it exists)
      const filteredSelection = prev?.filter((email) => !selectedEmailThreadPk || email?.pk !== selectedEmailThreadPk);
      // Check if the new mail is already in the selection
      const newMailExists = filteredSelection?.some((email) => email?.pk === mail?.pk && email?.sk === mail?.sk);
      // Add the new mail if it doesn't exist
      if (!newMailExists) {
        return [...(filteredSelection || []), { pk: mail?.pk, sk: mail?.sk, email_thread_id: mail?.email_thread_id }];
      }
      return filteredSelection || [];
    });
  };
  // if (!allMails?.length) {
  //   return (
  //     <div className="text-message-height mailbox">
  //       <div className="msg-holder pt-0" style={{ overflow: 'hidden auto' }}>
  //         <div className="d-flex align-items-center justify-content-center mt-3">
  //           <h6 className="text-center">No emails found</h6>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  const statusMapping = {
    SUCCESS: 'Mapped By AI',
    BYPASSED: 'Mapping Bypassed',
    FAILED: 'AI Mapping Failed',
    MANUAL_OVERRIDE: 'Mapped By User',
    NO_MATCH_FOUND: 'Unmapped By AI',
    IN_PROGRESS: 'Mapping In Progress',
    QUEUED: 'Mapping Scheduled',
    LAWFT_ORIGIN: 'Originated in Lawft',
    FAILED_MAIL: 'Failed Mail Deliveries',
  };

  const getStatusClass = (status) => {
    if (status === 'SUCCESS') return 's-tag'; // Success
    if (status === 'FAILED' || status === 'FAILED_MAIL') return 'da-tag'; // Failure
    if (status === 'IN_PROGRESS') return 'b-tag'; // In Progress
    return 'y-tag'; // All others
  };

  const mailListItemTemplate = (mail) => {
    // Guard clause: return null if mail is invalid
    if (!mail) return null;

    const isUnread = mail?.is_unread && !readEmails[mail?.pk];
    const isChecked = selectedEmails.some((email) => email.pk === mail.pk && email.sk === mail.sk);

    return (
      <div
        className="mail-list-outer-box inner-border-bottom d-flex align-items-stretch"
        style={{
          backgroundColor: selectedEmailThreadPk === mail?.pk ? 'var(--selected-msg)' : 'var(--white)',
        }}
      >
        <button
          className="flex-grow-1 p-2 pointer mail-list-box border-0 text-start"
          style={{
            backgroundColor: selectedEmailThreadPk === mail?.pk ? 'var(--selected-msg)' : 'var(--white)',
          }}
          tabIndex="0"
          onClick={() => {
            handleEmailClick(mail);
          }}
        >
          <div className="d-flex flex-column gap-1 w-100">
            <div className="d-flex align-items-center gap-2 justify-content-between">
              <div className="d-flex align-items-center gap-2 flex-grow-1 min-w-0">
                {multiple && (
                  <div className="d-flex align-items-center flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleEmailSelection(mail.pk, mail.sk, mail.email_thread_id);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                )}
                <div className="position-relative flex-shrink-0">
                  <ImageComponent
                    fileName={mail?.email_from?.user_email?.charAt(0)}
                    style={{ backgroundColor: '#2196F3', color: '#ffffff' }}
                    fileSize="medium.jpg"
                  />
                  {isUnread && <i className="icon-window-filled mail-list-badge"></i>}
                </div>
                <h6 className={`text-truncate black-700 mb-0 ${isUnread ? 'unread-email' : ''}`}>
                  {mail?.email_from?.user_name || mail?.email_from?.user_email}
                </h6>
              </div>

              {/* AI tags on rightmost */}
              {(mail?.ai_reconciliation_status || mail?.failed_delivery || mail?.lawft_origin) && (
                <div className="flex-shrink-0">
                  <Tag
                    className={`tag-case mail-ai-tags text-center d-status-tags d-flex ${getStatusClass(
                      mail?.failed_delivery ? 'FAILED_MAIL' : mail.ai_reconciliation_status
                    )}`}
                    rounded
                    value={
                      <>
                        <i className="bi bi-stars me-2"></i>
                        {statusMapping[
                          mail?.failed_delivery ? 'FAILED_MAIL' : mail?.lawft_origin ? 'LAWFT_ORIGIN' : mail?.ai_reconciliation_status
                        ] || mail?.ai_reconciliation_status}
                      </>
                    }
                  />
                </div>
              )}
            </div>

            {/* Second row: Subject */}
            <div className={`${isUnread ? 'unread-email' : ''} ms-5 ps-3`}>
              <span className={`pointer text-truncate d-block black-700 ${isUnread && 'text-bold'}`}>{mail?.subject}</span>
            </div>

            {/* Third row: Case Name */}
            {mail?.case_id && (
              <div className={`${isUnread ? 'unread-email' : ''} ms-5 ps-3`}>
                <span className={`text-truncate black-700 mb-2 mt-2`}>
                  Case/Lead:
                  <Link
                    to={`${mail?.is_lead ? '/leads' : '/cases'}/${mail?.case_id}/mails`}
                    className={`redirection-link-no-text-transform ellipsis-text case-name-tooltip-${mail?.index}`}
                  >
                    {' '}
                    {mail?.case_name}
                  </Link>
                </span>
                <Tooltip target={`.case-name-tooltip-${mail?.index}`} className="case-tooltip" content={mail?.case_name} position="top" />
              </div>
            )}

            {/* Third row: Date and Time */}
            <div className="d-flex gap-2 ms-5 ps-3 mt-1 justify-content-between">
              <span className="text-hint black-600 m-0">
                {convertDateFromApiToUserTimezone(mail?.time, userContext?.userDetails?.timezone, 'MM-DD-YYYY')}
              </span>
              <span className="text-hint black-600 m-0">
                {convertDateFromApiToUserTimezone(mail?.time, userContext?.userDetails?.timezone, 'hh:mm A')}
              </span>
            </div>
          </div>
        </button>
      </div>
    );
  };

  return (
    <div className="mailbox">
      <div className="msg-holder pt-0 pb-0 position-relative flex-grow-1" style={{ overflow: 'hidden' }}>
        {(loading || paginationLoading) && (
          <div className="chat-loader-container pe-3">
            <div className="overlay">
              <i className="pi pi-spin pi-spinner" style={{ fontSize: '2em' }}></i>
            </div>
          </div>
        )}
        <DataScroller
          value={allMails || []}
          emptyMessage="No emails found"
          itemTemplate={mailListItemTemplate}
          rows={20}
          inline
          lazy={allMails?.length >= 20}
          scrollHeight={scrollHeight}
          onLazyLoad={handleLoadMore}
        />
      </div>
    </div>
  );
};

export default MailList;
